import { useQuery, UseQueryResult } from '@edreamsodigeo/frontend-api-client';
import { useLogFrontendApiError } from '@edreamsodigeo/frontend-core';
import { usePerformedSearchRequest } from 'clientState/providers/SearchRequestProvider';
import { usePrime } from 'clientState/providers/PrimeProvider';
import { startOfDay, isBefore, parseISO } from 'date-fns';

import {
  FlexibleDatesQuery,
  FlexibleDatesQueryVariables,
} from 'generated/graphql';
import React, { createContext, useContext } from 'react';
import { FLEXIBLE_DATES } from '../operations/flexibleDates';

type FlexibleDatesProviderType = Partial<
  UseQueryResult<FlexibleDatesQuery, FlexibleDatesQueryVariables>
>;
const DEFAULT_STATE = { loading: false, data: null, errors: null };
const FlexibleDatesContext =
  createContext<FlexibleDatesProviderType>(DEFAULT_STATE);
export const FLEXIBLE_DATES_NUMBER_OF_DAYS = 3;
export const FLEXIBLE_DATES_PRICE_FRESHNESS_DAYS = 1;

const getRequestParams = (
  searchRequest,
  isPrimeSite
): FlexibleDatesQueryVariables | undefined => {
  if (!searchRequest) return undefined;
  const { itinerary, tripType } = searchRequest;
  return {
    flexibleDatesSearchRequest: {
      departureDate: itinerary?.segments?.[0]?.date,
      returnDate: itinerary?.segments?.[1]?.date,
      originGeoNode: itinerary?.segments?.[0]?.departure?.geoNodeId,
      destinationGeoNode: itinerary?.segments?.[0]?.destination?.geoNodeId,
      tripType: tripType,
      numberOfDaysFlexibility: FLEXIBLE_DATES_NUMBER_OF_DAYS,
      priceFreshnessDays: FLEXIBLE_DATES_PRICE_FRESHNESS_DAYS,
      priceCalculationCriteria: isPrimeSite
        ? 'MINIMUM_PURCHASABLE_MEMBERSHIP_PRICE'
        : 'MINIMUM_PURCHASABLE_PRICE',
    },
  };
};

export const FlexibleDatesProvider: React.FC = ({ children }) => {
  const searchRequest = usePerformedSearchRequest();
  const { isPrimeSite } = usePrime();

  const skipCriteria = (): boolean => {
    if (searchRequest?.tripType === undefined) {
      return true;
    }

    if (searchRequest?.tripType === 'ONE_WAY') {
      return (
        isBefore(
          parseISO(searchRequest?.itinerary?.segments?.[0]?.date),
          startOfDay(new Date())
        ) ||
        !searchRequest?.itinerary?.segments?.[0]?.departure?.geoNodeId ||
        !searchRequest?.itinerary?.segments?.[0]?.destination?.geoNodeId
      );
    }

    if (searchRequest?.tripType === 'ROUND_TRIP') {
      return (
        isBefore(
          parseISO(searchRequest?.itinerary?.segments?.[0]?.date),
          startOfDay(new Date())
        ) ||
        isBefore(
          parseISO(searchRequest?.itinerary?.segments?.[1]?.date),
          startOfDay(new Date())
        ) ||
        !searchRequest?.itinerary?.segments?.[0]?.departure?.geoNodeId ||
        !searchRequest?.itinerary?.segments?.[0]?.destination?.geoNodeId ||
        !searchRequest?.itinerary?.segments?.[1]?.departure?.geoNodeId ||
        !searchRequest?.itinerary?.segments?.[1]?.destination?.geoNodeId
      );
    }

    return true;
  };

  const flexibleDatesQuery = useQuery<
    FlexibleDatesQuery,
    FlexibleDatesQueryVariables
  >(FLEXIBLE_DATES, {
    variables: getRequestParams(searchRequest, isPrimeSite),
    skip: skipCriteria(),
    notifyOnBackgroundFetching: true,
  });

  return (
    <FlexibleDatesContext.Provider value={flexibleDatesQuery}>
      {children}
    </FlexibleDatesContext.Provider>
  );
};

export const useFlexibleDates = ():
  | FlexibleDatesQuery['flexibleDates']
  | null => {
  const result = useContext(FlexibleDatesContext);
  useLogFrontendApiError(result);
  const { errors, loading, data } = result;

  if (errors || loading) {
    return null;
  }

  return data?.flexibleDates || null;
};

export default FlexibleDatesProvider;
